import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  FileDownload as ExportIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ShoppingCart,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Order, apiService } from '../../services/apiService';

const OrdersManagement: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Order>>({});

  // Order drawer states
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [orderFormData, setOrderFormData] = useState({
    customer_id: '',
    item_name: '',
    unit_price: '',
    quantity: '',
    campaign_name: '',
    voucher_name: '',
    voucher_number: '',
    period: '',
    created_by: '',
    creation_date: '',
    coordinated_by: '',
    coordinated_date: '',
    feedback_by: '',
    feedback_date: '',
    effective_start_date: '',
    effective_end_date: '',
    followed_by: '',
    follow_status: '',
    collection_date: '',
    collection_time: '',
    notes: '',
    status: 'pending',
  });
  const [items, setItems] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch data for order drawer
  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        const [itemsResponse, usersResponse, customersResponse] = await Promise.all([
          apiService.getItems(),
          apiService.getUsers(),
          apiService.getCustomers(),
        ]);
        setItems(itemsResponse.data || []);
        setUsers(usersResponse.data || []);
        setCustomers(customersResponse.data || []);
      } catch (error: any) {
        console.error('Error fetching order data:', error);
      }
    };
    
    fetchOrderData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getOrders();
      setOrders(response.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, order: Order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrder(null);
  };

  const handleAddNew = () => {
    setEditingOrder(null);
    setOrderFormData({
      customer_id: '',
      item_name: '',
      unit_price: '',
      quantity: '',
      campaign_name: '',
      voucher_name: '',
      voucher_number: '',
      period: '',
      created_by: '',
      creation_date: '',
      coordinated_by: '',
      coordinated_date: '',
      feedback_by: '',
      feedback_date: '',
      effective_start_date: '',
      effective_end_date: '',
      followed_by: '',
      follow_status: '',
      collection_date: '',
      collection_time: '',
      notes: '',
      status: 'pending',
    });
    setOrderDrawerOpen(true);
  };

  const handleViewClick = (order: Order) => {
    navigate(`/orders/${order.id}`);
  };

  const handleRowClick = (order: Order) => {
    navigate(`/orders/${order.id}`);
  };

  const handleEditClick = (order?: Order) => {
    const orderToEdit = order || selectedOrder;
    if (orderToEdit) {
      setEditingOrder(orderToEdit);
      setOrderFormData({
        customer_id: orderToEdit.customer_id?.toString() || '',
        item_name: orderToEdit.item_name || '',
        unit_price: orderToEdit.unit_price?.toString() || '',
        quantity: orderToEdit.quantity?.toString() || '',
        campaign_name: orderToEdit.campaign_name || '',
        voucher_name: orderToEdit.voucher_name || '',
        voucher_number: orderToEdit.voucher_number || '',
        period: orderToEdit.period || '',
        created_by: orderToEdit.created_by || '',
        creation_date: orderToEdit.creation_date || '',
        coordinated_by: orderToEdit.coordinated_by || '',
        coordinated_date: orderToEdit.coordinated_date || '',
        feedback_by: orderToEdit.feedback_by || '',
        feedback_date: orderToEdit.feedback_date || '',
        effective_start_date: orderToEdit.effective_start_date || '',
        effective_end_date: orderToEdit.effective_end_date || '',
        followed_by: orderToEdit.followed_by || '',
        follow_status: orderToEdit.follow_status || '',
        collection_date: orderToEdit.collection_date || '',
        collection_time: orderToEdit.collection_time || '',
        notes: orderToEdit.notes || '',
        status: orderToEdit.status || 'pending',
      });
      setOrderDrawerOpen(true);
      setAnchorEl(null);
    }
  };

  const handleDeleteClick = (order?: Order) => {
    if (order) {
      Swal.fire({
        title: 'Are you sure?',
        text: `Are you sure you want to delete order "${order.item_name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await apiService.deleteOrder(order.id);
            toast.success('Order deleted successfully');
            fetchData();
          } catch (error: any) {
            toast.error(error.message || 'Failed to delete order');
          }
        }
      });
    }
  };

  const handleFormChange = (field: keyof Order, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (isEditing && selectedOrder) {
        await apiService.updateOrder(selectedOrder.id, formData);
        toast.success('Order updated successfully');
      } else {
        await apiService.createOrder(formData);
        toast.success('Order created successfully');
      }
      setDialogOpen(false);
      setFormData({});
      setIsEditing(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save order');
    }
  };

  // Order drawer handlers
  const handleCloseOrderDrawer = () => {
    setOrderDrawerOpen(false);
    setEditingOrder(null);
  };

  const handleOrderFormChange = (field: string, value: any) => {
    setOrderFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOrderSubmit = async () => {
    try {
      // Format collection_time to H:i:s format
      let formattedCollectionTime = orderFormData.collection_time;
      if (formattedCollectionTime && formattedCollectionTime.length === 5) {
        // If time is in HH:MM format, add :00 for seconds
        formattedCollectionTime = formattedCollectionTime + ':00';
      }

      const orderData = {
        ...orderFormData,
        customer_id: parseInt(orderFormData.customer_id) || 0,
        unit_price: parseFloat(orderFormData.unit_price) || 0,
        quantity: parseFloat(orderFormData.quantity) || 0,
        status: orderFormData.status as 'pending' | 'in_progress' | 'completed' | 'cancelled',
        collection_time: formattedCollectionTime || '',
      };

      if (editingOrder) {
        await apiService.updateOrder(editingOrder.id, orderData);
        toast.success('Order updated successfully');
      } else {
        await apiService.createOrder(orderData);
        toast.success('Order created successfully');
      }
      
      handleCloseOrderDrawer();
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save order');
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setFormData({});
    setIsEditing(false);
    setSelectedOrder(null);
  };

  // Filter orders based on search term and status
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.teckit_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.voucher_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return { bg: '#fef3c7', color: '#d97706' };
      case 'in_progress':
        return { bg: '#dbeafe', color: '#2563eb' };
      case 'completed':
        return { bg: '#e0f2f1', color: '#0d9488' };
      case 'cancelled':
        return { bg: '#fee2e2', color: '#dc2626' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Orders Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New Order
        </Button>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                    Total Orders
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#1f2937', fontWeight: 'bold' }}>
                    {orders.length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '12px', 
                  backgroundColor: '#e0f2f1', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <ShoppingCart sx={{ color: '#0d9488', fontSize: 24 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                    Active Orders
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#1f2937', fontWeight: 'bold' }}>
                    {orders.filter(order => order.status === 'in_progress').length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '12px', 
                  backgroundColor: '#dbeafe', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Typography variant="h6" sx={{ color: '#2563eb', fontWeight: 'bold' }}>
                    {orders.filter(order => order.status === 'in_progress').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                    Completed Orders
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#1f2937', fontWeight: 'bold' }}>
                    {orders.filter(order => order.status === 'completed').length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '12px', 
                  backgroundColor: '#e0f2f1', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Typography variant="h6" sx={{ color: '#0d9488', fontWeight: 'bold' }}>
                    {orders.filter(order => order.status === 'completed').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ 
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
        borderRadius: '16px',
        backgroundColor: 'white',
        border: 'none'
      }}>
        <CardContent sx={{ p: 3 }}>
          {/* Search and Action Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <TextField
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#f8fafc',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #e2e8f0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #cbd5e1',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #4A90E2',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#6b7280' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 120, height: '48px' }}>
              <InputLabel>Filter</InputLabel>
              <Select
                label="Filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ 
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#f8fafc',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #e2e8f0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #cbd5e1',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #4A90E2',
                  },
                }}
              >
                <MenuItem value="all">All Orders</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              sx={{
                height: '48px',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 500,
                borderColor: '#e2e8f0',
                color: '#64748b',
                px: 3,
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              Export
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0', borderRadius: '12px', height: 'calc(100vh - 450px)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell padding="checkbox">
                    <Checkbox />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Teckit No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Campaign</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Created Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOrders.map((order) => (
                  <TableRow 
                    key={order.id} 
                    hover 
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8fafc', cursor: 'pointer' },
                      cursor: 'pointer'
                    }}
                    onClick={() => handleRowClick(order)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox />
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>{order.teckit_no || 'N/A'}</TableCell>
                    <TableCell sx={{ color: '#374151' }}>{order.item_name}</TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {order.customer ? `${order.customer.company_name || order.customer.contact_person}` : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>{order.campaign_name || 'N/A'}</TableCell>
                    <TableCell sx={{ color: '#374151' }}>{order.quantity || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(order.status).bg,
                          color: getStatusColor(order.status).color,
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {formatDate(order.created_at)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => handleViewClick(order)}
                          sx={{
                            backgroundColor: '#dbeafe',
                            color: '#2563eb',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2,
                            py: 1,
                            '&:hover': {
                              backgroundColor: '#bfdbfe',
                            },
                          }}
                        >
                          View
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleEditClick(order)}
                          sx={{
                            backgroundColor: '#e0f2f1',
                            color: '#0d9488',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2,
                            py: 1,
                            '&:hover': {
                              backgroundColor: '#ccedea',
                            },
                          }}
                        >
                          Update
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleDeleteClick(order)}
                          sx={{
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2,
                            py: 1,
                            '&:hover': {
                              backgroundColor: '#fecaca',
                            },
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredOrders.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </CardContent>
      </Card>

      {/* Order Drawer */}
      <Drawer
        anchor="right"
        open={orderDrawerOpen}
        onClose={handleCloseOrderDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 1000 },
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e2937' }}>
            {editingOrder ? 'Edit Order' : 'Create New Order'}
          </Typography>

          {/* Customer Selection */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Customer Information
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={customers}
                getOptionLabel={(customer) => `${customer.name} - ${customer.email}`}
                value={customers.find(c => c.id === (editingOrder?.customer_id || orderFormData.customer_id)) || null}
                onChange={(event, newValue) => {
                  handleOrderFormChange('customer_id', newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer"
                    required
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Item Details */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Item Details
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={items}
                getOptionLabel={(item) => `${item.name} - ${item.item_code}`}
                value={items.find(i => i.name === orderFormData.item_name) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleOrderFormChange('item_name', newValue.name);
                    handleOrderFormChange('unit_price', newValue.unit_price.toString());
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Item"
                    required
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={orderFormData.unit_price}
                onChange={(e) => handleOrderFormChange('unit_price', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={orderFormData.quantity}
                onChange={(e) => handleOrderFormChange('quantity', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={orderFormData.campaign_name}
                onChange={(e) => handleOrderFormChange('campaign_name', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Voucher Name"
                value={orderFormData.voucher_name}
                onChange={(e) => handleOrderFormChange('voucher_name', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Voucher Number"
                value={orderFormData.voucher_number}
                onChange={(e) => handleOrderFormChange('voucher_number', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Period"
                value={orderFormData.period}
                onChange={(e) => handleOrderFormChange('period', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={orderFormData.status}
                  onChange={(e) => handleOrderFormChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Order Management */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Order Management
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => user.name}
                value={users.find(u => u.name === orderFormData.created_by) || null}
                onChange={(event, newValue) => {
                  handleOrderFormChange('created_by', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Created By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Creation Date"
                type="date"
                value={orderFormData.creation_date}
                onChange={(e) => handleOrderFormChange('creation_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => user.name}
                value={users.find(u => u.name === orderFormData.coordinated_by) || null}
                onChange={(event, newValue) => {
                  handleOrderFormChange('coordinated_by', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Coordinated By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Coordinated Date"
                type="date"
                value={orderFormData.coordinated_date}
                onChange={(e) => handleOrderFormChange('coordinated_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => user.name}
                value={users.find(u => u.name === orderFormData.feedback_by) || null}
                onChange={(event, newValue) => {
                  handleOrderFormChange('feedback_by', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Feedback By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Feedback Date"
                type="date"
                value={orderFormData.feedback_date}
                onChange={(e) => handleOrderFormChange('feedback_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Effective Start Date"
                type="date"
                value={orderFormData.effective_start_date}
                onChange={(e) => handleOrderFormChange('effective_start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Effective End Date"
                type="date"
                value={orderFormData.effective_end_date}
                onChange={(e) => handleOrderFormChange('effective_end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => user.name}
                value={users.find(u => u.name === orderFormData.followed_by) || null}
                onChange={(event, newValue) => {
                  handleOrderFormChange('followed_by', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Followed By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Follow Status"
                value={orderFormData.follow_status}
                onChange={(e) => handleOrderFormChange('follow_status', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Collection Date"
                type="date"
                value={orderFormData.collection_date}
                onChange={(e) => handleOrderFormChange('collection_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Collection Time"
                type="time"
                value={orderFormData.collection_time}
                onChange={(e) => handleOrderFormChange('collection_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={orderFormData.notes}
                onChange={(e) => handleOrderFormChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleCloseOrderDrawer}
              sx={{ px: 4 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleOrderSubmit}
              sx={{ px: 4 }}
            >
              {editingOrder ? 'Update Order' : 'Create Order'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default OrdersManagement;